import { useErrorStore } from "@/state/pinia/errorStore";
import { useAuthStore } from "@/state/pinia";
import router from "@/router";
import { swal } from "@/helpers/handle_error";

const DEFAULT_MESSAGE = "حدث خطأ غير متوقع";

const statusMessages = {
    400: "طلب غير صحيح",
    401: "غير مصرح لك بالوصول. يرجى تسجيل الدخول",
    403: "ليس لديك صلاحية للقيام بهذا الإجراء",
    404: "البيانات المطلوبة غير موجودة",
    422: "بيانات غير صحيحة",
    429: "تم تجاوز الحد المسموح من الطلبات. يرجى المحاولة لاحقا",
    500: "خطأ في الخادم. يرجى المحاولة لاحقا",
    503: "الخدمة غير متاحة حاليا"
};

const flattenErrors = (errors) => {
    if (!errors) return [];
    if (Array.isArray(errors)) return errors;
    if (typeof errors === "object") return Object.values(errors).flat();
    return [String(errors)];
};

export const normalizeRequestError = (error) => {
    let message = DEFAULT_MESSAGE;
    let details = [];
    let status = null;

    if (typeof error === "string") {
        return { message: error, details: [], status: null };
    }

    if (error?.response) {
        status = error.response.status ?? null;
        const data = error.response.data ?? {};

        if (data.message) message = data.message;
        if (data.errors) details = flattenErrors(data.errors);

        if (status && statusMessages[status]) {
            message = data.message || statusMessages[status];
        } else if (status) {
            message = data.message || `خطأ في الخادم (${status})`;
        }

        return { message, details, status };
    }

    if (error?.message) {
        const lowerMessage = error.message.toLowerCase();
        if (error.message.includes("Network Error") || lowerMessage.includes("network") || lowerMessage.includes("failed to fetch")) {
            message = "خطأ في الاتصال بالشبكة. يرجى التحقق من الاتصال بالإنترنت";
        } else {
            message = error.message;
        }
    }

    return { message, details, status };
};

export const pushRequestError = (error, options = {}) => {
    swal.close();
    const { ignoreStatuses = [] } = options;

    const normalized = normalizeRequestError(error);

    console.groupCollapsed("[RequestError]");
    console.error("Raw error:", error);
    console.log("Status:", normalized.status);
    console.log("Message:", normalized.message);
    console.log("Details:", normalized.details);
    if (error?.response) {
        console.log("Response data:", error.response.data);
        console.log("Response headers:", error.response.headers);
        console.log("Request config:", error.config);
    }
    console.groupEnd();

    // If the request is unauthorized, force a client-side logout and redirect to login.
    if (normalized.status == 401 || normalized.status == 403) {
        const authStore = useAuthStore();
        if (typeof authStore.logOutClientSide === 'function') {
            authStore.logOutClientSide();
        }
        router.replace({ name: 'login' });
    }

    // Ignore specific HTTP status codes if requested (e.g., 404)
    if (normalized.status && ignoreStatuses.includes(normalized.status)) {
        return {
            errors: [],
            normalized,
            ignored: true
        };
    }

    const messages = [];
    const errorStore = useErrorStore();
    const isNetworkError = error?.message && (error.message.includes("Network Error") || error.message.toLowerCase().includes("network"));

    // Handle validation errors (422) - each error message as separate item
    if (normalized.status === 422 && normalized.details.length > 0) {
        const validationMessages = normalized.details
            .map(detail => typeof detail === "object" ? detail.message : detail)
            .filter(Boolean);
        
        if (validationMessages.length > 0) {
            messages.push(validationMessages);
        } else if (normalized.message) {
            messages.push(normalized.message);
        }
    } else if (isNetworkError) {
        // For network errors, check if the error already exists in the store
        const networkErrorMessage = "خطأ في الاتصال بالشبكة. يرجى التحقق من الاتصال بالإنترنت";
        const errorExists = errorStore.errors.some(storedError => 
            storedError === networkErrorMessage || 
            (Array.isArray(storedError) && storedError.includes(networkErrorMessage))
        );
        
        // Only add the network error if it doesn't already exist
        if (!errorExists) {
            messages.push(networkErrorMessage);
        }
    } else {
        // For other statuses, keep the original behavior
        if (normalized.message) messages.push(normalized.message);
        if (normalized.details.length > 0) messages.push(...normalized.details);
    }

    errorStore.pushMessages(messages);

    return {
        errors: errorStore.errors,
        normalized,
        ignored: false
    };
};

export const pushFileRequestError = (error, options = {}) => {
    const {
        isCancel = null,
        pushCancelMessage = false,
        cancelMessage = "تم إلغاء الطلب"
    } = options;

    if (typeof isCancel === "function" && isCancel(error)) {
        if (pushCancelMessage) {
            const errorStore = useErrorStore();
            errorStore.pushMessages([cancelMessage]);
            return { canceled: true, errors: errorStore.errors };
        }

        return { canceled: true, errors: [] };
    }

    const result = pushRequestError(error);
    return { canceled: false, ...result };
};

export const initGlobalErrorHandler = () => {
    const frontendErrorMessage = "حدث خطأ غير متوقع. يرجى تحديث الصفحة والمحاولة مرة أخرى";
    
    // Handle uncaught JavaScript errors
    window.addEventListener("error", (event) => {
        const errorStore = useErrorStore();
        
        console.groupCollapsed("[FrontendError]");
        console.error("Frontend error:", event.error);
        if (event.error instanceof Error) {
            console.error("Error stack:", event.error.stack);
        }
        console.groupEnd();
        
        // Check if error already exists
        const errorExists = errorStore.errors.some(storedError => 
            storedError === frontendErrorMessage
        );
        
        if (!errorExists) {
            errorStore.pushMessages([frontendErrorMessage]);
        }
    });

    // Handle unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
        const errorStore = useErrorStore();
        
        console.groupCollapsed("[UnhandledPromiseRejection]");
        console.error("Promise rejection:", event.reason);
        console.groupEnd();
        
        // Check if error already exists
        const errorExists = errorStore.errors.some(storedError => 
            storedError === frontendErrorMessage
        );
        
        if (!errorExists) {
            errorStore.pushMessages([frontendErrorMessage]);
        }
    });
};
